import { supabase, isSupabaseConfigured, type Application } from '../lib/supabase';

export class ApplicationService {

  static async uploadResume(file: File, applicantEmail: string): Promise<string | null> {
    if (!isSupabaseConfigured() || !supabase) {
      console.warn('Supabase not configured, skipping resume upload');
      return null;
    }

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${applicantEmail.replace('@', '_')}_${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading file:', error);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('resumes')
        .getPublicUrl(data.path);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error in uploadResume:', error);
      return null;
    }
  }

  static async submitApplication(applicationData: Omit<Application, 'id' | 'created_at'>): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        success: false,
        error: 'Database not configured. Please contact support to complete setup.'
      };
    }

    try {
      const { error } = await supabase
        .from('applications')
        .insert([applicationData])
        .select();

      if (error) {
        console.error('Error submitting application:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in submitApplication:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  static async getApplications(): Promise<Application[]> {
    if (!isSupabaseConfigured() || !supabase) {
      console.warn('Supabase not configured, cannot fetch applications');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching applications:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getApplications:', error);
      return [];
    }
  }
}
